import * as z from "zod";
import { isBlankCoordinate, isLatitudeInRange, isLongitudeInRange, parseCoordinate } from "@/utils/coordinates";

interface ValidateCoordinatePairOptions {
  /**
   * Prefix applied to the emitted message keys.
   *
   * The two order-creation flows resolve validation errors differently: the market flow does
   * `t(`validation:${message}`)` at the call site, while the contract flow does `t(message)` and
   * expects a fully-qualified key. Pass "validation:" for the latter and "" for the former.
   */
  keyPrefix?: string;
  latitudePath?: string;
  longitudePath?: string;
}

/**
 * Validate an optional latitude/longitude pair held as free-text form values.
 *
 * Call this from inside a schema's existing `superRefine` rather than chaining another one, so it
 * composes with the address/delivery rules already in place.
 *
 * Coordinates are optional: both blank is valid. But a lone latitude is meaningless, so a
 * half-filled pair is rejected on the blank field.
 */
export function validateCoordinatePair(
  latitude: string | null | undefined,
  longitude: string | null | undefined,
  ctx: z.RefinementCtx,
  options: ValidateCoordinatePairOptions = {}
): void {
  const { keyPrefix = "", latitudePath = "latitude", longitudePath = "longitude" } = options;
  const key = (name: string) => `${keyPrefix}${name}`;

  const addIssue = (message: string, path: string) =>
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: key(message), path: [path] });

  const latitudeBlank = isBlankCoordinate(latitude);
  const longitudeBlank = isBlankCoordinate(longitude);

  // Coordinates are optional — nothing entered is a valid order.
  if (latitudeBlank && longitudeBlank) return;

  if (latitudeBlank) addIssue("coordinatesBothRequired", latitudePath);
  if (longitudeBlank) addIssue("coordinatesBothRequired", longitudePath);

  if (!latitudeBlank) {
    const parsed = parseCoordinate(latitude);
    if (parsed === null) {
      addIssue("coordinateInvalidFormat", latitudePath);
    } else if (!isLatitudeInRange(parsed)) {
      addIssue("latitudeOutOfRange", latitudePath);
    }
  }

  if (!longitudeBlank) {
    const parsed = parseCoordinate(longitude);
    if (parsed === null) {
      addIssue("coordinateInvalidFormat", longitudePath);
    } else if (!isLongitudeInRange(parsed)) {
      addIssue("longitudeOutOfRange", longitudePath);
    }
  }
}
